import { captureMessage } from "@sentry/nextjs";
import { timeDiff } from "@website/utils/date-format/date-format.server";
import {
  DateFormatType,
  TransformDateType
} from "@website/utils/date-format/date-format.types";
import { isFilled } from "@website/utils/is-filled";
import { numberConvert } from "@website/utils/number-convert";
import padEnd from "lodash-es/padEnd";
import padStart from "lodash-es/padStart";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import { DateTime } from "luxon";

const calendars: Record<string, string> = {
  en: "iso8601",
  fa: "persian",
  ar: "islamic"
};

const dateParser = (date: string) => {
  if (/^-?\d{10,13}$/.test(date) || date === "0") {
    return new Date(
      toNumber(padEnd(date, date.startsWith("-") ? 14 : 13, "0"))
    );
  }
  return new Date(
    date
      .replaceAll(
        /(\d{4})[/-](\d{1,2})[/-](\d{1,2})/g,
        (match, year, month, day) =>
          `${year}-${padStart(month, 2, "0")}-${padStart(day, 2, "0")}`
      )
      .replaceAll(
        /(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\b/g,
        (match, hour, minute, second) =>
          `${hour}:${minute.padStart(2, "0")}${
            second ? `:${second.padStart(2, "0")}` : ""
          }`
      )
  );
};

export const today = () =>
  dateFormat(Date.now() + timeDiff, {
    locale: "en",
    template: "yyyy-MM-dd"
  });

export const tomorrow = () =>
  dateFormat(Date.now() + timeDiff + 60 * 60 * 24 * 1000, {
    locale: "en",
    template: "yyyy-MM-dd"
  });

export const isInvalidCheckin = (
  date_from: unknown,
  date_to: unknown,
  isEmptyAllowed?: boolean
) =>
  (isEmptyAllowed === false || Boolean(date_from) || Boolean(date_to)) &&
  (!date_from ||
    !date_to ||
    !(daysBetweenDates(date_from, date_to) >= 1) ||
    !(daysBetweenDates(today(), date_from) >= 0) ||
    !(daysBetweenDates(tomorrow(), date_to) >= 0));

export const daysBetweenDates = (start: unknown, end: unknown) => {
  try {
    const a = new Date(start as Date);
    const b = new Date(end as Date);
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
  } catch {
    captureMessage(`daysBetweenDates went wrong => start:${start} end:${end}`);
  }
  return 0;
};

export const dateFormat: DateFormatType = (date, options) => {
  const locale = options.locale ?? "en";
  const parsedDate = dateParser(toString(date));
  if (!isFilled(date) || !isFilled(parsedDate)) {
    return undefined;
  }
  if ([undefined, "timestamp"].includes(options?.template)) {
    return parsedDate.getTime();
  }
  return numberConvert(
    DateTime.fromJSDate(parsedDate)
      .reconfigure({ outputCalendar: calendars[locale] })
      .setLocale(locale)
      .setZone("Asia/Tehran")
      .toFormat(options?.template),
    {
      numberOnly: false,
      locale: options.numberConvert === false ? "en" : locale
    }
  );
};

export const getDiffInMonth = (dateFrom: Date, dateTo: Date) => {
  let monthsDiff = dateTo.getMonth() - dateFrom.getMonth();
  const yearsMonths = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12;
  if (dateTo.getDate() < dateFrom.getDate()) {
    monthsDiff -= 1;
  }
  return monthsDiff + yearsMonths;
};

export const getDiffMonthBasedOnDay = (dateFrom: string, dateTo: string) => {
  const startDate = DateTime.fromISO(dateFrom);
  const endDate = DateTime.fromISO(dateTo);
  return endDate.diff(startDate, "months").months;
};

export const transformDate: TransformDateType = (
  date,
  { year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0 } = {}
): string => {
  date.setFullYear(date.getFullYear() + year);
  date.setMonth(date.getMonth() + month);
  date.setDate(date.getDate() + day);
  date.setHours(date.getHours() + hour);
  date.setMinutes(date.getMinutes() + minute);
  date.setSeconds(date.getSeconds() + second);

  return date.toISOString();
};

export const formatToPersianDate = (
  date: string,
  config?: Intl.DateTimeFormatOptions
) => {
  if (!date) return "";
  const defaultConfig: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    timeZone: "UTC"
  };
  const UTC = "+00:00";
  const TIME = "T00:00:00";
  const dateObject =
    date.length === 10
      ? new Date(`${date}${TIME}${UTC}`)
      : new Date(`${date}${UTC}`);

  return new Intl.DateTimeFormat(
    "fa-IR",
    config
      ? {
          timeZone: "UTC",
          ...config
        }
      : defaultConfig
  ).format(dateObject);
};

export const getFormattedFutureDate = (searchWindow: number) => {
  const now = new Date();
  const utcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + (searchWindow || 0)
  );
  return new Date(utcMidnight).toISOString().slice(0, 10);
};

export const getTotalMinutes = (dateFrom: string, dateTo: string) => {
  const date1 = new Date(dateFrom);
  const date2 = new Date(dateTo);

  const diffMs = Math.abs(date2.valueOf() - date1.valueOf());

  const totalMinutes = Math.floor(diffMs / 1000 / 60);
  return totalMinutes;
};

export const getDiffInSeconds = (dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) return 0;
  const date1 = new Date(dateFrom);
  const date2 = new Date(dateTo);

  const diffMs = date2.valueOf() - date1.valueOf();
  return Math.floor(diffMs / 1000);
};

export const getDiffInMinutes = (dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) return 0;
  const totalMinutes = getTotalMinutes(dateFrom, dateTo);
  return Math.floor(totalMinutes % 60);
};

export const getDiffInHours = (dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) return 0;
  const totalMinutes = getTotalMinutes(dateFrom, dateTo);
  return Math.floor(totalMinutes / 60);
};

export const formatSecondToTime = (seconds: number) => {
  const safeSeconds = Math.max(0, seconds);
  const totalMinutes = Math.floor(safeSeconds / 60);
  const totalSeconds = Math.floor(safeSeconds % 60);

  return `${totalMinutes >= 10 ? totalMinutes : `0${totalMinutes}`}:${
    totalSeconds >= 10 ? totalSeconds : `0${totalSeconds}`
  }`;
};
